<template>
  <div
    class="drawer"
    :class="{
      'drawer--collapsed': collapsed,
      'drawer--student': isStudentView,
    }"
  >
    <div
      id="drawer-inner"
      ref="drawerInner"
      class="tw-flex tw-flex-col tw-overflow-y-auto tw-overflow-x-hidden"
    >
      <div class="drawer--logo">
        <router-link class="tw-flex tw-flex-1 tw-items-center" to="/">
          <collabsible-logo />
        </router-link>
      </div>

      <div
        class="tw-flex tw-flex-col tw-overflow-auto tw-p-4"
        :class="{ 'tw-gap-1': collapsed, 'tw-gap-4': !collapsed }"
      >
        <div
          v-for="{ title, items } in sections"
          class="tw-flex tw-flex-col tw-gap-1"
        >
          <div
            v-if="title && !collapsed"
            class="tw-flex tw-h-[32px] tw-items-center tw-px-4 tw-text-[14px] tw-text-gray-500"
          >
            {{ title }}
          </div>

          <menu-link
            v-for="(item, key) in items"
            :key="key"
            :item="item"
            :collapsed
            student-view
          />
        </div>
      </div>

      <div class="drawer--collapse">
        <menu-link
          v-for="item in collapseMenuItems"
          :key="item.meta.title"
          :item="item"
          :collapsed
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import MenuLink from "components/layout/MenuLink.vue";
import { useClassroomNavigationStore } from "classroom/stores";
import { HOME_ROUTE_NAMES } from "home/routes/routeNames";
import { MENU_OWN_SECTION_ROUTE_NAMES } from "shared/constants";
import useHelpers from "composables/useHelpers";
import { debounce } from "lodash";
import { storeToRefs } from "pinia";
import { SENTRY_FEEDBACK_BUTTON_AS_MENU_ITEM } from "plugins/sentry";
import { useMenuStore } from "store/modules/menuStore.js";
import { computed, onBeforeUnmount, onMounted, useTemplateRef } from "vue";
import CollabsibleLogo from "./CollabsibleLogo.vue";

// menuStore
const menuStore = useMenuStore();
const {
  menuItems,
  organiseItems,
  manageItems,
  impersonatingItems,
  collapsed,
  collapseMenuItem,
  sentryFeedbackMenuItem,
} = storeToRefs(menuStore);

const classroomNavigationStore = useClassroomNavigationStore();
const { menuGroups: classroomMenuGroups, canSeeClassroomMenu } = storeToRefs(
  classroomNavigationStore,
);

const drawerInner = useTemplateRef("drawerInner");
let drawerInnerResizeObserver = null;

const { checkOverlapAndHide, checkRole, isDevelopmentEnvironment } =
  useHelpers();

const isStudentView = computed(() => checkRole("student"));

// Home is de enige ingang die docent en leerling delen, dus de route draagt de
// docentvorm. `studentView` is wat MenuLink de gekleurde 40x40-tegel en de
// leerlingopmaak laat tekenen; de andere leerlingroutes zetten het zelf.
const homeMenuItems = computed(() =>
  menuItems.value
    .filter((item) => item.name === HOME_ROUTE_NAMES.MAIN)
    .map((item) =>
      isStudentView.value === false
        ? item
        : {
            ...item,
            meta: {
              ...item.meta,
              icon: "/images/menu/home.svg",
              studentView: true,
            },
          },
    ),
);

const meetingsMenuItems = computed(() =>
  menuItems.value.filter((item) => item.name === "myMeetings"),
);

const remainingMenuItems = computed(() =>
  menuItems.value.filter(
    (item) => MENU_OWN_SECTION_ROUTE_NAMES.includes(item.name) === false,
  ),
);

const sections = computed(() => [
  // Secties staan 16px uit elkaar, items binnen een sectie 4px. Het
  // leerlingmenu is een doorlopende lijst, dus daar hoort Home bij de rest in
  // plaats van in een eigen sectie; het docentmenu houdt zijn scheiding.
  ...(homeMenuItems.value.length && isStudentView.value === false
    ? [{ title: null, items: homeMenuItems.value }]
    : []),
  ...(meetingsMenuItems.value.length
    ? [{ title: null, items: meetingsMenuItems.value }]
    : []),
  ...(canSeeClassroomMenu.value
    ? [{ title: null, items: classroomMenuGroups.value }]
    : []),
  {
    title: null,
    items: isStudentView.value
      ? [...homeMenuItems.value, ...remainingMenuItems.value]
      : remainingMenuItems.value,
  },
  ...(organiseItems.value.length
    ? [{ title: "Organiseren", items: organiseItems.value }]
    : []),
  ...(manageItems.value.length
    ? [{ title: "Beheren", items: manageItems.value }]
    : []),
  ...(impersonatingItems.value.length
    ? [{ title: null, items: impersonatingItems.value }]
    : []),
]);

const collapseMenuItems = computed(() => {
  return [
    !isDevelopmentEnvironment && SENTRY_FEEDBACK_BUTTON_AS_MENU_ITEM
      ? sentryFeedbackMenuItem.value
      : null,
    collapseMenuItem.value,
  ].filter(Boolean);
});

const onScreenResize = debounce(() => {
  checkResize();
}, 100);

const checkResize = () => {
  const w = window.innerWidth || document.documentElement.clientWidth;

  if (w >= 1024 && w < 1280) {
    collapsed.value = true;
  } else if (w >= 1280) {
    collapsed.value = localStorage.getItem("menu_collapsed") === "true";
  }
};

onMounted(async () => {
  checkResize();

  if (canSeeClassroomMenu.value) {
    classroomNavigationStore.loadClasses();
  }

  window.addEventListener("resize", checkOverlap);
  window.addEventListener("resize", onScreenResize);

  drawerInnerResizeObserver = new ResizeObserver(() => {
    checkOverlap();
  });

  drawerInnerResizeObserver.observe(drawerInner.value);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", checkOverlap);
  window.removeEventListener("resize", onScreenResize);

  drawerInnerResizeObserver.disconnect();
});

const checkOverlap = debounce(() => {
  const sentryButton = document
    .querySelector("#sentry-feedback")
    ?.shadowRoot?.querySelector("button.widget__actor");

  if (!sentryButton) {
    return;
  }

  checkOverlapAndHide(drawerInner.value, sentryButton);
}, 500);
</script>

<style lang="scss" scoped>
.drawer {
  @apply tw-hidden tw-w-full tw-max-w-[270px] tw-flex-col tw-overflow-hidden tw-border-r tw-bg-white tw-pb-[12px] tw-transition-all tw-duration-300 tw-ease-in-out lg:tw-flex;

  &--collapsed {
    @apply tw-w-full tw-max-w-[72px];
  }

  &--student.drawer--collapsed {
    @apply tw-max-w-[88px];
  }

  &--logo {
    @apply tw-mx-4 tw-flex tw-h-[64px] tw-shrink-0 tw-flex-col tw-justify-center;
  }

  &--collapse {
    @apply tw-mx-4 tw-flex tw-flex-col tw-gap-1 tw-pt-4;
  }
}
</style>
